import React, { MouseEvent, useCallback, useMemo, useState } from "react";

import {
  TFunction,
  formatNumber,
  formatNumericCurrency,
  useCancelAsyncOnUnmount,
  useDeleteField,
  useGetAllCarbonProgramOutcomes,
  useGetAllFieldPracticesByFarm,
  useGetFarms,
  useGetFieldDetails,
  useTranslation,
} from "utils";

import { ACCOUNTING_METHODOLOGY } from "models";

import { Button, Card, Drawer, FCPOMenu, Flex, HideFCPOFeatureForUnenrolledFarms, Icon, Modal, OpenTaskButton, Spin, Tag, TooltipWrapper } from "components";

import { useSubmissionStatus } from "pages/app/all-field-view/hooks/useSubmissionStatus";
import { FieldDetails } from "pages/app/fields/components/FieldDetails";

type FieldCellProps = {
  areOutcomesValid: boolean;
  currentAvgPayouts: number;
  fieldId: string;
  fieldName: string;
  hasFieldOutcomes: boolean;
  hasLocation: boolean;
  isRedZone: boolean;
  size: number;
  shouldShowPayout: boolean;
  onMissingDataClick: () => void;
  className?: string;
  dataTestId?: string;
  activateCheckbox?: boolean;
  inView?: boolean;
  isEnrolled?: boolean;
  onClick?: () => void;
  openTasksCount?: number;
  hasFieldOpenTasks?: boolean;
};

const getCellStates = (name: string, hasLocation: boolean, t: TFunction<["farm", "allFieldView"], undefined>) => {
  if (!name) {
    return {
      nameError: t("farm:unknownFieldName"),
      iconName: "klim/edit-element",
      hasError: true,
      className: "red-text",
    };
  }
  if (!hasLocation) {
    return {
      locationError: t("farm:unknownLocation"),
      iconName: "klim/map-pin-plus",
      hasError: true,
      className: "red-text",
    };
  }

  return {
    nameError: null,
    locationError: null,
    size: null,
    iconName: null,
    hasError: false,
    className: null,
  };
};

export const FieldCell = ({
  fieldId,
  fieldName,
  hasLocation,
  isRedZone,
  size,
  onClick,
  onMissingDataClick,
  hasFieldOutcomes = false,
  areOutcomesValid = false,
  className = "",
  currentAvgPayouts = 0,
  isEnrolled = false,
  openTasksCount,
  hasFieldOpenTasks,
  shouldShowPayout,
}: FieldCellProps) => {
  const { t } = useTranslation(["farm"]);
  const { currentFarm } = useGetFarms();
  const { data: allFieldsPractices, isLoading: isLoadingAllFieldsPractices } = useGetAllFieldPracticesByFarm(currentFarm);
  const { data: field } = useGetFieldDetails({ id: fieldId });
  const { mutate: deleteField } = useDeleteField();
  const signal = useCancelAsyncOnUnmount()();
  const { isFetching: isFetchingCarbonProgramOutcomes, progress: carbonProgramOutcomesProgress } = useGetAllCarbonProgramOutcomes(
    currentFarm?.id,
    currentFarm?.carbonProgram
  );

  const { isSubmissionFeatureEnabled } = useSubmissionStatus();

  const [showFieldEditDrawer, setShowFieldEditDrawer] = useState(false);
  const [showDeleteFieldModal, setShowDeleteFieldModal] = useState(false);

  const practices = allFieldsPractices?.[fieldId];
  const fieldHasActivePractices = !!practices?.availablePracticeBundles.some((bundle) => bundle.active);
  const cellStates = useMemo(() => getCellStates(fieldName, hasLocation, t), [fieldName, hasLocation, t]);
  const hectareLimitWouldBeExceeded = !field || (field.exceedsHectareLimit && !fieldHasActivePractices);
  const isInventoryProgram = currentFarm?.carbonProgram?.accountingMethodology === ACCOUNTING_METHODOLOGY.inventory;
  const isPracticeEnrollmentDisabled = !isInventoryProgram && (hectareLimitWouldBeExceeded || !currentFarm?.carbonProgram?.isEnabled);

  const handleMissingDataClick = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    // if there are field related open tasks, we want to resolve those before navigating to the crops with missing data.
    if (isSubmissionFeatureEnabled && hasFieldOpenTasks) {
      setShowFieldEditDrawer(true);
      return;
    }
    onMissingDataClick();
  };

  const onModalSubmit = useCallback(() => {
    deleteField({
      fieldId: fieldId || "",
      signal,
    });

    return setShowDeleteFieldModal(false);
  }, [deleteField, fieldId, signal]);

  const getOpenTaskUI = () => {
    if (openTasksCount) {
      return (
        <OpenTaskButton
          count={openTasksCount}
          text={t("allFieldView:fieldCell.addData")}
          isLoading={isFetchingCarbonProgramOutcomes || carbonProgramOutcomesProgress !== 100}
          onClick={handleMissingDataClick}
          dataTestId="add-data-btn"
        />
      );
    }
    if (isInventoryProgram) {
      return <Icon iconName={"tabler/check"} className={"icon inline checkmark-icon"} size="s" />;
    }
    if (!isInventoryProgram && isEnrolled && shouldShowPayout) {
      return (
        <span className="paragraph-s-bold green-text" data-testid="field-payouts">
          {formatNumericCurrency(currentAvgPayouts, { currency: currentFarm?.carbonProgram?.currency })}
        </span>
      );
    }
    return null;
  };

  const NewSubmissionFlowFieldCell = () => (
    <div className="field-cell-layout">
      <Flex className="field-cell-layout-main" gap={"0.25rem"}>
        <Flex className="field-cell-layout-top" justify="space-between">
          <Flex vertical={true} justify="flex-start">
            <TooltipWrapper
              className="field-cell-tooltip"
              tooltip={
                isPracticeEnrollmentDisabled
                  ? {
                      title: (
                        <span className="field-cell-tooltip-content">
                          {hectareLimitWouldBeExceeded ? t("allFieldView:fieldCell.tooltipHaLimit") : t("allFieldView:fieldCell.tooltipPayout")}
                        </span>
                      ),
                      placement: "top",
                    }
                  : null
              }
              data-testid={"field-cell-tooltip"}
            >
              <p className={`paragraph-m-bold margin-0 field-name ${cellStates.nameError ? cellStates.className : ""}`}>{fieldName || cellStates.nameError}</p>
            </TooltipWrapper>
            <Flex>
              {cellStates.locationError ? (
                <span className={cellStates.locationError ? cellStates.className : ""}>{cellStates.locationError}</span>
              ) : (
                <Flex gap={"0.5rem"}>
                  <p className="paragraph-s grey-text margin-0 ">{formatNumber({ number: size })} ha</p>
                  <p className="paragraph-s grey-text margin-0">{isRedZone && t("farm:redzone")}</p>
                </Flex>
              )}
            </Flex>
          </Flex>
          <Flex style={{ margin: "-1rem" }}>
            <FCPOMenu
              availablePracticeBundles={practices?.availablePracticeBundles || []}
              fieldId={fieldId}
              isEnrolled={isEnrolled}
              setShowFieldEditDrawer={setShowFieldEditDrawer}
              setShowDeleteFieldModal={setShowDeleteFieldModal}
              fieldDetails={field}
              carbonProgram={currentFarm?.carbonProgram ?? null}
              isPracticeEnrollmentDisabled={isPracticeEnrollmentDisabled}
              placement="right"
            />
          </Flex>
        </Flex>
        <Flex className="field-cell-layout-bottom">
          {isEnrolled && !isInventoryProgram && <Tag colour="green-200" text={t("enrolled")} />}
          <div style={{ marginLeft: "auto" }}>{getOpenTaskUI()}</div>
        </Flex>
      </Flex>
    </div>
  );

  const OldSubmissionFlowFieldCell = () => (
    <div className="field-cell-layout">
      <Flex className="field-cell-layout-main">
        <Flex className="field-cell-layout-top">
          <TooltipWrapper
            className="field-cell-tooltip"
            tooltip={
              isPracticeEnrollmentDisabled
                ? {
                    title: (
                      <span className="field-cell-tooltip-content">
                        {hectareLimitWouldBeExceeded ? t("allFieldView:fieldCell.tooltipHaLimit") : t("allFieldView:fieldCell.tooltipPayout")}
                      </span>
                    ),
                    placement: "top",
                  }
                : null
            }
            data-testid={"field-cell-tooltip"}
          >
            <p className={`paragraph-m-bold field-name ${cellStates.nameError ? cellStates.className : ""}`}>{fieldName || cellStates.nameError}</p>
          </TooltipWrapper>
          <Flex>
            <FCPOMenu
              availablePracticeBundles={practices?.availablePracticeBundles || []}
              fieldId={fieldId}
              isEnrolled={isEnrolled}
              setShowFieldEditDrawer={setShowFieldEditDrawer}
              setShowDeleteFieldModal={setShowDeleteFieldModal}
              fieldDetails={field}
              carbonProgram={currentFarm?.carbonProgram ?? null}
              isPracticeEnrollmentDisabled={isPracticeEnrollmentDisabled}
              placement="right"
            />
            {isEnrolled && !isInventoryProgram && (
              <Icon iconName="tabler/certificate" className="icon inline klim-icon" dataTestId="field-cell-enrollment-icon" />
            )}
          </Flex>
        </Flex>
      </Flex>
      <Flex className="field-cell-layout-bottom">
        <div className="bottom-text">
          {cellStates.locationError ? (
            <span className={cellStates.locationError ? cellStates.className : ""}>{cellStates.locationError}</span>
          ) : (
            <>
              <span className="grey-text">{formatNumber({ number: size })} ha</span>
              <span className="grey-text">{isRedZone && t("farm:redzone")}</span>
              <HideFCPOFeatureForUnenrolledFarms>
                {isEnrolled && hasFieldOutcomes && !isInventoryProgram ? (
                  areOutcomesValid ? (
                    <span className="paragraph-s-bold green-text" data-testid="field-payouts">
                      {formatNumericCurrency(currentAvgPayouts, { currency: currentFarm?.carbonProgram?.currency })}
                    </span>
                  ) : (
                    <Button className="field-cell-button" size="small" type="secondary" onClick={handleMissingDataClick} dataTestId="add-data-btn">
                      {t("allFieldView:fieldCell.addData")}
                    </Button>
                  )
                ) : null}
              </HideFCPOFeatureForUnenrolledFarms>
            </>
          )}
        </div>
      </Flex>
    </div>
  );

  return isLoadingAllFieldsPractices ? (
    <Flex align="center" justify="center" style={{ height: "100px" }}>
      <Spin />
    </Flex>
  ) : (
    <div className="width-100" style={{ height: "100%" }}>
      <Card onClick={onClick} className={`field-cell ${className}`} mixpanelText="field-cell" dataTestId="field-cell">
        <div className="field-cell-content">
          <div className="field-cell-layout">{isSubmissionFeatureEnabled ? <NewSubmissionFlowFieldCell /> : <OldSubmissionFlowFieldCell />}</div>
        </div>
      </Card>
      <Drawer title={t("editFieldDrawerTitle")} visible={showFieldEditDrawer} onClose={() => setShowFieldEditDrawer(false)}>
        <FieldDetails fieldId={fieldId} setShowFieldEditDrawer={setShowFieldEditDrawer} />
      </Drawer>
      <Modal
        showModal={showDeleteFieldModal}
        onOk={() => {
          onModalSubmit();
          setShowDeleteFieldModal(false);
        }}
        onCancel={() => {
          setShowDeleteFieldModal(false);
        }}
        okText={t("farm:deleteModalConfirm.deleteText")}
        cancelText={t("farm:deleteModalConfirm.cancel")}
        zIndex={9999}
        destroyOnHidden={true}
        title={
          <>
            <Icon iconName="tabler/alert-circle" className="inline icon margin-right-75" size="m" fill="#DB3804" color="#FFF" />
            <span className="headline-3">{t("farm:deleteModalConfirm.fieldTitle", { fieldName: fieldName })}</span>
          </>
        }
        content={<span className="paragraph-m">{t("farm:deleteModalConfirm.fieldMessage", { fieldName: fieldName })}</span>}
      />
    </div>
  );
};
